import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, forwardRef, Input, Output, signal } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractBaseFieldComponent } from '@shared/components/form-elements/abstract-field-base';
import { FormErrorsModule } from '@shared/components/form-elements/form-errors/form-errors.module';

@Component({
  selector: 'cx-input-quantity',
  imports: [CommonModule, FormsModule, FormErrorsModule],
  templateUrl: './input-quantity.component.html',
  styleUrls: ['./input-quantity.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputQuantityComponent),
      multi: true,
    },
  ],
  host: {
    '[attr.id]': 'null',
  },
})
export class InputQuantityComponent extends AbstractBaseFieldComponent implements ControlValueAccessor, AfterViewInit {
  @Input() control?: AbstractControl | null;
  @Input() label = '';
  @Input() hintText = '';
  @Input() id = this.generateId();
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() min: number = 0;
  @Input() max?: number;
  @Input() valid?: boolean;
  @Input() invalid?: boolean;
  @Input() allowDeleteOnMin?: boolean;

  @Output() delete = new EventEmitter<void>();

  removeIcon = signal<'delete' | 'remove'>('remove');
  private focusValue: string = '';

  ngAfterViewInit(): void {
    this.updateRemoveIcon();
  }

  onFocus(): void {
    this.focusValue = this.value ?? '';
  }

  onBlur(): void {
    this.onTouched();
    if (this.value !== this.focusValue) {
      this.onValueChange();
    }
  }

  onValueChange() {
    if (Number(this.value) < this.min) {
      this.value = String(this.min);
    } else if (this.max !== undefined && Number(this.value) > this.max) {
      this.value = String(this.max);
    } else {
      this.onChange(this.value);
    }
    this.updateRemoveIcon();
  }

  updateRemoveIcon() {
    this.removeIcon.set(this.allowDeleteOnMin && Number(this.value) === this.min ? 'delete' : 'remove');
  }

  add(): void {
    const newValue = Number(this.value) + 1;
    if (this.max === undefined || newValue <= this.max) {
      this.value = String(newValue);
      this.onValueChange();
    }
  }

  remove(): void {
    const newValue = Number(this.value) - 1;
    if (newValue >= this.min) {
      this.value = String(newValue);
      this.onValueChange();
    } else if (this.allowDeleteOnMin) {
      this.delete.emit();
    }
  }
}
